export type AnalyticsData = Record<string, string | number | boolean>;

declare global {
  interface Window {
    umami?: {
      track(name: string, data?: AnalyticsData): void;
    };
  }
}

/**
 * Privacy-safe wrapper for Replit's published-app analytics tracker.
 * Calls are a no-op in development, before the tracker loads, or when
 * analytics is disabled. Analytics must never interrupt a user action.
 */
export function trackEvent(name: string, data?: AnalyticsData): void {
  if (typeof window === "undefined") return;

  try {
    window.umami?.track(name, data);
  } catch {
    // Analytics failures must never break the app.
  }
}

/**
 * Tracks a milestone only once per browser. The marker contains only the fixed
 * event name—never user or account data—and is written only when the published
 * app's tracker is available.
 */
export async function trackFirstEvent(name: string, data?: AnalyticsData): Promise<void> {
  if (typeof window === "undefined" || !window.umami) return;

  const storageKey = `incontrol.analytics.first.${name}`;
  try {
    const response = await fetch("/api/analytics/activation-milestones/claim", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ milestone: name }),
    });
    if (!response.ok) throw new Error("Milestone claim failed");

    const result = await response.json() as { isNew?: unknown };
    if (result.isNew !== true) return;

    window.umami?.track(name, data);
    window.localStorage.setItem(storageKey, "1");
  } catch {
    // Rollout fallback: retain the previous browser-only deduplication when the
    // claim endpoint is unavailable. Analytics must never break user actions.
    try {
      if (window.localStorage.getItem(storageKey) === "1") return;
      window.umami?.track(name, data);
      window.localStorage.setItem(storageKey, "1");
    } catch {
      // Analytics and storage failures must never break the app.
    }
  }
}
